import React, { useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, useScroll, useTransform } from 'framer-motion';
import { Calendar, Phone, Cat, BadgeCheck, PawPrint, ArrowRight, Cookie } from 'lucide-react';
import { BUSINESS } from '../../lib/data';
import Magnetic from '../ui/Magnetic';
import { usePhotos, useRotator } from '../../lib/photos';

const wordAnim = {
  hidden: { opacity: 0, y: 44, rotate: 3 },
  show: (i) => ({
    opacity: 1, y: 0, rotate: 0,
    transition: { duration: 0.65, delay: 0.12 + i * 0.085, ease: [0.21, 0.65, 0.36, 1] },
  }),
};
const fade = {
  hidden: { opacity: 0, y: 26 },
  show: (i) => ({ opacity: 1, y: 0, transition: { duration: 0.7, delay: 0.65 + i * 0.14, ease: [0.21, 0.65, 0.36, 1] } }),
};

function Word({ children, i, className = '' }) {
  return (
    <span className="inline-block overflow-visible">
      <motion.span custom={i} variants={wordAnim} className={`inline-block ${className}`}>
        {children}
      </motion.span>
    </span>
  );
}

// preserveAspectRatio="none" plus a fixed em height keep this an underline.
// Without them the SVG scales with its own width, so a long phrase grows the
// squiggle tall enough to strike straight through the text.
function Squiggle() {
  return (
    <svg viewBox="0 0 220 14" fill="none" preserveAspectRatio="none" className="absolute -bottom-[0.32em] left-0 w-full h-[0.3em]" aria-hidden>
      <motion.path
        d="M3 10 C 30 3, 55 3, 80 8 S 130 13, 160 7 S 200 4, 217 8"
        stroke="url(#sq)" strokeWidth="5" strokeLinecap="round"
        initial={{ pathLength: 0 }}
        animate={{ pathLength: 1 }}
        transition={{ duration: 1, delay: 1.1, ease: 'easeOut' }}
      />
      <defs>
        <linearGradient id="sq" x1="0" y1="0" x2="220" y2="0" gradientUnits="userSpaceOnUse">
          <stop stopColor="#ec4899" /><stop offset="1" stopColor="#06b6d4" />
        </linearGradient>
      </defs>
    </svg>
  );
}

// Rotating circular text badge — links to booking, spins forever.
function OrbitBadge() {
  return (
    <Link to="/booking" aria-label="Book a spa day" className="group absolute -bottom-8 -left-4 sm:-left-10 w-28 h-28 sm:w-36 sm:h-36 z-10">
      <motion.svg
        viewBox="0 0 100 100"
        animate={{ rotate: 360 }}
        transition={{ duration: 16, repeat: Infinity, ease: 'linear' }}
        className="w-full h-full drop-shadow-xl"
      >
        <defs>
          <path id="orbit" d="M50,50 m-38,0 a38,38 0 1,1 76,0 a38,38 0 1,1 -76,0" />
          <linearGradient id="og" x1="0" y1="0" x2="1" y2="1">
            <stop stopColor="#ec4899" /><stop offset="1" stopColor="#06b6d4" />
          </linearGradient>
        </defs>
        <circle cx="50" cy="50" r="49" fill="white" />
        <text fill="url(#og)" fontSize="9.4" fontWeight="800" letterSpacing="1.5">
          <textPath href="#orbit">BOOK A SPA DAY • BAKERY TREATS • </textPath>
        </text>
      </motion.svg>
      <span className="absolute inset-0 flex items-center justify-center">
        <span className="bg-gradient-to-br from-pink-500 to-cyan-500 rounded-full p-3 sm:p-3.5 shadow-lg group-hover:scale-110 transition-transform">
          <PawPrint className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
        </span>
      </span>
    </Link>
  );
}

export default function Hero() {
  const { hero } = usePhotos();
  const slide = useRotator(hero.length);
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ['start start', 'end start'] });
  const yPhoto = useTransform(scrollYProgress, [0, 1], [0, 110]);
  const scalePhoto = useTransform(scrollYProgress, [0, 1], [1, 1.13]);
  const yWord = useTransform(scrollYProgress, [0, 1], [0, -90]);
  const fadeCopy = useTransform(scrollYProgress, [0, 0.7], [1, 0]);
  const yCopy = useTransform(scrollYProgress, [0, 1], [0, 60]);

  return (
    <section ref={ref} className="relative overflow-hidden grain min-h-[94svh] flex items-center">
      <div aria-hidden className="absolute inset-0 bg-gradient-to-br from-pink-50 via-white to-cyan-50" />
      <div aria-hidden className="absolute -top-40 -right-32 w-[34rem] h-[34rem] rounded-full bg-gradient-to-br from-pink-300/40 to-purple-300/30 blur-3xl" />
      <div aria-hidden className="absolute -bottom-40 -left-32 w-[30rem] h-[30rem] rounded-full bg-gradient-to-tr from-cyan-300/40 to-pink-200/30 blur-3xl" />

      <motion.p
        aria-hidden
        style={{ y: yWord }}
        className="pointer-events-none select-none absolute top-8 left-1/2 -translate-x-1/2 whitespace-nowrap font-display font-black text-outline text-[22vw] leading-none tracking-tight"
      >
        woof woof
      </motion.p>

      <div className="relative w-full max-w-7xl mx-auto px-4 pt-14 pb-24 md:py-12 grid lg:grid-cols-[1.05fr_1fr] gap-16 lg:gap-6 items-center">
        <motion.div
          initial="hidden"
          animate="show"
          style={{ opacity: fadeCopy, y: yCopy }}
          className="text-center lg:text-left"
        >
          <motion.div custom={0} variants={fade} className="inline-flex items-center space-x-2 bg-white/90 backdrop-blur border border-pink-200 text-pink-600 px-5 py-2 rounded-full text-xs md:text-sm font-extrabold uppercase tracking-[0.18em] shadow-sm">
            <PawPrint className="w-4 h-4" />
            <span>Orlando's Premium Pet Spa</span>
          </motion.div>

          {/* Fluid below sm: "Pawsitively happy." is a long single line and
              overflowed narrow phones at a fixed 2.9rem. */}
          <h1 className="mt-7 font-display font-black leading-[1.02] text-[clamp(1.7rem,8.2vw,2.9rem)] sm:text-6xl lg:text-[4.7rem] text-ink-900 tracking-tight">
            <Word i={0}>Pampered.</Word> <Word i={1}>Polished.</Word>
            <br />
            <span className="relative inline-block">
              <Word i={2} className="italic bg-gradient-to-r from-pink-500 via-purple-500 to-cyan-500 bg-clip-text text-transparent pr-1">
                Pawsitively&nbsp;happy.
              </Word>
              <Squiggle />
            </span>
          </h1>

          <motion.p custom={1} variants={fade} className="mt-6 text-lg md:text-xl text-gray-600 max-w-xl mx-auto lg:mx-0 leading-relaxed">
            Luxury grooming, gentle hands and a bakery counter of pet-safe treats —
            <span className="font-bold text-gray-800"> trusted by pet parents, loved by pets.</span>
          </motion.p>

          <motion.div custom={2} variants={fade} className="mt-9 flex flex-col sm:flex-row items-center justify-center lg:justify-start gap-4">
            <Magnetic className="w-full sm:w-auto">
              <Link
                to="/booking"
                className="btn-shine w-full inline-flex items-center justify-center space-x-2.5 bg-gradient-to-r from-pink-500 to-cyan-500 text-white px-9 py-4 rounded-full font-extrabold text-lg shadow-2xl shadow-pink-300/50 transition-transform duration-300 hover:scale-[1.03]"
              >
                <Calendar className="w-5 h-5" />
                <span>Book a Spa Day</span>
                <ArrowRight className="w-5 h-5" />
              </Link>
            </Magnetic>
            <a
              href={BUSINESS.phoneHref}
              className="w-full sm:w-auto inline-flex items-center justify-center space-x-2.5 bg-white/90 backdrop-blur border-2 border-ink-900/10 hover:border-pink-300 text-ink-900 px-9 py-4 rounded-full font-extrabold text-lg shadow-lg transition-all duration-300"
            >
              <Phone className="w-5 h-5 text-pink-500" />
              <span>{BUSINESS.phone}</span>
            </a>
          </motion.div>

          <motion.div custom={3} variants={fade} className="mt-9 flex flex-wrap items-center justify-center lg:justify-start gap-x-7 gap-y-2.5 text-sm font-bold text-gray-500">
            <span className="flex items-center space-x-1.5"><BadgeCheck className="w-4 h-4 text-cyan-500" /><span>Certified groomers</span></span>
            <span className="flex items-center space-x-1.5"><Cat className="w-4 h-4 text-pink-500" /><span>Dogs &amp; cats welcome</span></span>
            <span className="flex items-center space-x-1.5"><Cookie className="w-4 h-4 text-amber-500" /><span>Bakery treats</span></span>
          </motion.div>
        </motion.div>

        {/* photo scene with scroll-scrub zoom */}
        <motion.div style={{ y: yPhoto }} className="relative mx-auto w-full max-w-[26rem] lg:max-w-[30rem]">
          <div aria-hidden className="absolute -inset-6 rounded-full border-2 border-dashed border-pink-300/50 animate-spin-slow" />
          <div aria-hidden className="absolute -inset-3 bg-gradient-to-br from-pink-400/50 via-purple-300/40 to-cyan-400/50 blur-2xl animate-blob" />

          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.25, ease: [0.21, 0.65, 0.36, 1] }}
            className="relative overflow-hidden animate-blob shadow-2xl shadow-purple-300/40 bg-gradient-to-br from-pink-100 via-white to-cyan-100"
          >
            <motion.div style={{ scale: scalePhoto }} className="relative w-full h-[24rem] sm:h-[27rem] lg:h-[30rem]">
              {hero.map((photo, i) => (
                <motion.div
                  key={photo.src}
                  aria-hidden={i !== slide}
                  initial={false}
                  animate={{ opacity: i === slide ? 1 : 0 }}
                  transition={{ duration: 1.1, ease: 'easeInOut' }}
                  className="absolute inset-0"
                >
                  {/* A blurred copy fills the blob so no photo has to be cropped
                      to fit it, and the shape still reads as a solid shape. */}
                  <img
                    src={photo.src}
                    alt=""
                    aria-hidden
                    className="absolute inset-0 w-full h-full object-cover scale-110 blur-2xl opacity-70"
                  />
                  <img
                    src={photo.src}
                    alt={i === slide ? photo.alt : ''}
                    fetchpriority={i === 0 ? 'high' : 'low'}
                    className="relative w-full h-full object-contain drop-shadow-2xl"
                  />
                </motion.div>
              ))}
            </motion.div>
            {/* brand grade */}
            <div aria-hidden className="absolute inset-0 bg-gradient-to-tr from-purple-500/20 via-transparent to-cyan-400/15 mix-blend-multiply" />
          </motion.div>

          <OrbitBadge />
        </motion.div>
      </div>

      {/* scroll hint */}
      <motion.div
        aria-hidden
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.6 }}
        className="absolute bottom-5 left-1/2 -translate-x-1/2 hidden md:block"
      >
        <motion.div
          animate={{ y: [0, 8, 0] }}
          transition={{ duration: 1.6, repeat: Infinity, ease: 'easeInOut' }}
          className="w-6 h-10 rounded-full border-2 border-ink-900/20 flex items-start justify-center p-1.5"
        >
          <div className="w-1.5 h-2.5 rounded-full bg-gradient-to-b from-pink-500 to-cyan-500" />
        </motion.div>
      </motion.div>
    </section>
  );
}
